import {
  IGlobalVocabularyView,
  IVocabularyPreviewView,
  IVocabularyRow,
  IVocabularySaveRequest,
  IVocabularySuggestionsView,
} from "types/BenefitSelection/BenefitSelection";

import { api } from ".";

/**
 * CLE-2792 — `BenefitLineVocabulary`: how a payer identifies a benefit LINE TYPE.
 *
 * SuperAdmin on the reads as well as the writes, because a definition is a statement about a payer
 * that applies to every account that payer serves — adding one gives a physician copay to estimates
 * that carry none today.
 *
 * ONE SURFACE OWNS THESE NOW: Eligibility › Line types. Review round 1 removed the copy on the payer
 * edit page, because two editors for the same rows meant a reader could see half the effective set
 * and think it was all of it. `GET` with no `payerId` returns EVERY row, global and payer-scoped.
 */
const benefitLineVocabularyQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getGlobalLineVocabulary: builder.query<
      { data: IGlobalVocabularyView; message: string },
      void
    >({
      query: () => "benefitLineVocabulary",
      providesTags: [
        { type: "BenefitLineVocabulary" as const, id: "GLOBAL" },
        { type: "BenefitLineVocabulary" as const, id: "LIST" },
      ],
    }),
    /**
     * The wording this payer actually sends, most frequent first — the chip row in the dialog.
     * A read over stored responses; no pVerify call and no PII column.
     */
    getLineVocabularySuggestions: builder.query<
      { data: IVocabularySuggestionsView; message: string },
      { payerId: number; serviceLine: string }
    >({
      query: ({ payerId, serviceLine }) =>
        `benefitLineVocabulary/suggestions?payerId=${encodeURIComponent(
          payerId
        )}&serviceLine=${encodeURIComponent(serviceLine)}`,
    }),
    /** What an unsaved row would match. Counted by the engine's own matcher, not a restatement. */
    previewLineVocabulary: builder.mutation<
      { data: IVocabularyPreviewView; message: string },
      IVocabularySaveRequest
    >({
      query: (body) => ({
        url: "benefitLineVocabulary/preview",
        method: "POST",
        body,
      }),
    }),
    /**
     * 🚨 `payerIds` WRITES ONE ROW PER PAYER, and `null`/`[]` writes the every-payer row. The
     * dialog's "Applies to" control is that field; a definition that has to be re-typed per payer
     * is one that gets typed differently per payer. The response is the rows it created — one per
     * payer named, so a caller cannot read a multi-payer create as having written a single row.
     */
    createLineVocabulary: builder.mutation<
      { data: IVocabularyRow[]; message: string },
      IVocabularySaveRequest
    >({
      query: (body) => ({
        url: "benefitLineVocabulary",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "BenefitLineVocabulary" as const, id: "LIST" }],
    }),
    /**
     * 🚨 THE BODY MUST CARRY `payerIds`. An absent one is read as "re-scope this row to every
     * payer" rather than as "leave the scope alone" — see `IVocabularySaveRequest`.
     */
    updateLineVocabulary: builder.mutation<
      { data: IVocabularyRow; message: string },
      { id: string; body: IVocabularySaveRequest }
    >({
      query: ({ id, body }) => ({
        url: `benefitLineVocabulary/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "BenefitLineVocabulary" as const, id: "LIST" }],
    }),
    /** 🚨 A HARD DELETE — the table has no versioning. The API writes the row to `EventLog` first. */
    deleteLineVocabulary: builder.mutation<
      { data: { id: string }; message: string },
      string
    >({
      query: (id: string) => ({
        url: `benefitLineVocabulary/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "BenefitLineVocabulary" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetGlobalLineVocabularyQuery,
  useGetLineVocabularySuggestionsQuery,
  usePreviewLineVocabularyMutation,
  useCreateLineVocabularyMutation,
  useUpdateLineVocabularyMutation,
  useDeleteLineVocabularyMutation,
} = benefitLineVocabularyQueries;
